import React from 'react';
import {
  ANALYTICS_COLORS,
  ANALYTICS_RANGE_PRESETS,
} from '../../../constants/analytics.constants';
import { AnalyticsOverview } from '../../../types/analytics.types';

interface AnalyticsRangeRowProps {
  activePresetId: string;
  onPresetChange: (presetId: string) => void;
  overview: AnalyticsOverview | null;
  isLoading: boolean;
  onRefresh: () => void;
}

interface InRangeMetricProps {
  label: string;
  value: number | null;
  color: string;
  isLoading: boolean;
}

const formatNumber = (value: number | null): string =>
  value === null ? '—' : value.toLocaleString();

const InRangeMetric: React.FC<InRangeMetricProps> = ({ label, value, color, isLoading }) => (
  <div className="flex items-center gap-2">
    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
    <span className="text-sm font-semibold text-slate-900 tabular-nums">
      {isLoading && value === null ? (
        <span className="inline-block h-4 w-8 rounded bg-slate-100 animate-pulse" />
      ) : (
        formatNumber(value)
      )}
    </span>
  </div>
);

export const AnalyticsRangeRow: React.FC<AnalyticsRangeRowProps> = ({
  activePresetId,
  onPresetChange,
  overview,
  isLoading,
  onRefresh,
}) => {
  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-2xl border border-slate-200/80 bg-white shadow-sm px-4 py-3">
      <div className="inline-flex rounded-xl bg-slate-50 border border-slate-200 p-1 self-start">
        {ANALYTICS_RANGE_PRESETS.map((preset) => {
          const isActive = preset.id === activePresetId;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => onPresetChange(preset.id)}
              disabled={isLoading}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors whitespace-nowrap ${
                isActive
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
              } ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-4">
        <InRangeMetric
          label="Created"
          value={overview?.created ?? null}
          color={ANALYTICS_COLORS.CREATED}
          isLoading={isLoading}
        />
        <div className="h-5 w-px bg-slate-200" />
        <InRangeMetric
          label="Updated"
          value={overview?.updated ?? null}
          color={ANALYTICS_COLORS.UPDATED}
          isLoading={isLoading}
        />
        <div className="h-5 w-px bg-slate-200" />
        <button
          type="button"
          onClick={onRefresh}
          disabled={isLoading}
          aria-label="Refresh analytics"
          title="Refresh"
          className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <svg
            className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};
